export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: unknown) {
    super(ApiError.describe(status, body));
  }

  /**
   * Prefer a human-readable `message` from the server when it sends one — a
   * refusal the user is meant to act on ("approve this row first") should not
   * surface as the bare machine code `not_approved`. Falls back to `error`.
   */
  private static describe(status: number, body: unknown): string {
    if (typeof body === "object" && body !== null) {
      const b = body as { message?: unknown; error?: unknown };
      if (typeof b.message === "string" && b.message.trim() !== "") return b.message;
      if ("error" in b) return String(b.error);
    }
    return `HTTP ${status}`;
  }
}

/**
 * Process-wide focus state set by ScopeContext. Injected as headers on every
 * fetch so the server's resolveDealerIdsInScope honors the picker without
 * each route having to plumb query params.
 */
let currentFocusGroupId: string | null = null;
let currentFocusDealerId: string | null = null;

export function setApiFocusHeaders(opts: {
  focusGroupId: string | null;
  focusDealerId: string | null;
}) {
  currentFocusGroupId = opts.focusGroupId;
  currentFocusDealerId = opts.focusDealerId;
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const focusHeaders: Record<string, string> = {};
  if (currentFocusGroupId) focusHeaders["X-Focused-Group-Id"] = currentFocusGroupId;
  if (currentFocusDealerId) focusHeaders["X-Focused-Dealer-Id"] = currentFocusDealerId;

  const res = await fetch(path, {
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
      ...focusHeaders,
      ...init.headers,
    },
    ...init,
  });
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (res.status === 401 && !path.startsWith("/api/auth/")) {
    // The session expired under an open tab (phones keep tabs alive for
    // weeks). Reload so AuthContext re-probes /me and RequireAuth lands on
    // /login with the current path as `from`, instead of the caller showing
    // an "approve failed: unauthorized" alert. /api/auth/* is excluded: the
    // /me probe and a wrong-password login handle their own 401s.
    window.location.reload();
    await new Promise(() => {}); // never resolves; the page is going away
  }
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const apiGet = <T = unknown>(path: string) => api<T>(path);
export const apiPost = <T = unknown>(path: string, body?: unknown) =>
  api<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
export const apiPut = <T = unknown>(path: string, body?: unknown) =>
  api<T>(path, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) });
export const apiDelete = <T = unknown>(path: string) =>
  api<T>(path, { method: "DELETE" });
